import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaEdit, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import OrderHistoryDateFilters, {
    defaultOrderHistoryFromDate,
    defaultOrderHistoryToDate,
    orderMatchesHistoryDateRange,
} from './OrderHistoryDateFilters';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import OrdersTablePagination from './OrdersTablePagination';
import OrderStatusPill from './ui/OrderStatusPill';
import ToolbarRichSelect, { PARTY_HALL_VIEW_OPTIONS } from './ToolbarRichSelect';
import {
    fetchPartyHallQuotesByRestaurant,
    isTerminalPartyHallQuoteStatus,
} from './partyHallQuoteApi';
import { buildSortHandlers } from './ordersTableUtils';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import './css/TodayOrder.css';
import './css/Menu.css';

export const PARTY_HALL_QUOTE_STATUS_OPTS = [
    { code: 'REQUESTED', label: 'Requested', matchNorm: ['REQUESTED'] },
    { code: 'UNDER_REVIEW', label: 'Under Review', matchNorm: ['UNDERREVIEW'] },
    { code: 'CONFIRMED', label: 'Confirmed', matchNorm: ['CONFIRMED'] },
    { code: 'REJECTED', label: 'Rejected', matchNorm: ['REJECTED'] },
];

function buildDefaultQuoteStatusSelections() {
    return Object.fromEntries(
        PARTY_HALL_QUOTE_STATUS_OPTS.filter((o) => o.code !== 'CONFIRMED').map((o) => [
            o.code,
            o.code === 'REQUESTED',
        ])
    );
}

function normalizeQuoteStatusNorm(quote) {
    return String(quote?.quoteStatus ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

function quotePassesStatusFilter(quote, selections, options) {
    const allSel = options.every((o) => selections[o.code]);
    if (allSel) return true;
    const anySel = options.some((o) => selections[o.code]);
    if (!anySel) return true;
    const norm = normalizeQuoteStatusNorm(quote);
    for (const opt of options) {
        if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
    }
    return false;
}

function quoteMatchesKeyword(quote, keyword) {
    const q = String(keyword ?? '').trim().toLowerCase();
    if (!q) return true;
    const parts = [
        quote.partyHallQuoteId,
        quote.customerId,
        quote.customerName,
        quote.customerPhone,
        quote.hallName,
        quote.hallType,
        quote.eventType,
        quote.eventDate,
        quote.quoteStatus,
        quote.estimatedTotal,
        quote.quotedTotal,
        quote.numberOfGuests,
        quote.amenityNotes,
        quote.specialRequests,
    ];
    return parts.filter(Boolean).join(' ').toLowerCase().includes(q);
}

function formatDateDisplay(raw) {
    if (!raw) return 'N/A';
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y, m, d] = s.slice(0, 10).split('-');
        return `${d}/${m}/${y}`;
    }
    try {
        const date = new Date(s);
        if (!Number.isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }
    } catch (e) {
        /* ignore */
    }
    return s;
}

function formatMoney(value) {
    if (value == null || value === '') return 'N/A';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toFixed(2);
}

function quoteSortValue(quote, key) {
    switch (key) {
        case 'orderId':
            return String(quote.partyHallQuoteId ?? '');
        case 'customerName':
            return quote.customerName ?? '';
        case 'customerPhone':
            return quote.customerPhone ?? '';
        case 'eventDate':
            return quote.eventDate ?? '';
        case 'eventType':
            return quote.eventType ?? '';
        case 'hallName':
            return quote.hallName ?? '';
        case 'numberOfGuests':
            return Number(quote.numberOfGuests) || 0;
        case 'totalPrice':
            return Number.parseFloat(quote.quotedTotal ?? quote.estimatedTotal) || 0;
        case 'orderStatus':
            return quote.quoteStatus ?? '';
        case 'createdDate':
            return quote.createdDate ?? '';
        default:
            return '';
    }
}

function sortQuoteList(list, sort) {
    if (!sort?.key) return list;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
        const va = quoteSortValue(a, sort.key);
        const vb = quoteSortValue(b, sort.key);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
}

const PartyHallPanel = ({
    restaurantId,
    ordersPageSize,
    onPageSizeChange,
    activeSection = 'quotes',
    onSectionChange,
    serviceChannelSelect = null,
}) => {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusSelections, setStatusSelections] = useState(buildDefaultQuoteStatusSelections);
    const [fromDate, setFromDate] = useState(defaultOrderHistoryFromDate);
    const [toDate, setToDate] = useState(defaultOrderHistoryToDate);
    const [sort, setSort] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const isOrdersView = activeSection === 'orders';

    const { handleSortAsc, handleSortDesc } = buildSortHandlers(setSort, () => setCurrentPage(1));

    const loadQuotes = useCallback(async () => {
        if (!restaurantId) {
            setQuotes([]);
            return;
        }
        setLoading(true);
        try {
            const rows = await fetchPartyHallQuotesByRestaurant(restaurantId);
            setQuotes(rows);
        } catch (err) {
            console.error('Failed to load party hall quotes:', err?.response?.data ?? err.message);
            setQuotes([]);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        loadQuotes();
    }, [loadQuotes]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeSection, search, statusSelections, fromDate, toDate]);

    const filteredQuotes = useMemo(() => {
        let list = quotes.filter((q) => quoteMatchesKeyword(q, search));
        if (isOrdersView) {
            list = list.filter((q) => normalizeQuoteStatusNorm(q) === 'CONFIRMED');
            list = list.filter((q) => orderMatchesHistoryDateRange(q, fromDate, toDate));
        } else {
            list = list.filter((q) => normalizeQuoteStatusNorm(q) !== 'CONFIRMED');
            list = list.filter((q) =>
                quotePassesStatusFilter(
                    q,
                    statusSelections,
                    PARTY_HALL_QUOTE_STATUS_OPTS.filter((o) => o.code !== 'CONFIRMED')
                )
            );
        }
        return sortQuoteList(list, sort);
    }, [quotes, search, statusSelections, sort, isOrdersView, fromDate, toDate]);

    const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / Math.max(ordersPageSize || 10, 1)));
    const page = Math.min(currentPage, totalPages);
    const currentQuotes = filteredQuotes.slice(
        (page - 1) * ordersPageSize,
        page * ordersPageSize
    );

    const toggleStatus = (code) => {
        setStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    };

    const setAllStatuses = (checked) => {
        setStatusSelections((prev) => {
            const next = { ...prev };
            PARTY_HALL_QUOTE_STATUS_OPTS.filter((o) => o.code !== 'CONFIRMED').forEach((opt) => {
                next[opt.code] = checked;
            });
            return next;
        });
    };

    const openEditQuote = (quote) => {
        if (!quote?.partyHallQuoteId) return;
        navigate(`/party-hall-quote/${quote.partyHallQuoteId}/edit`);
    };

    const handleSectionChange = (section) => {
        if (typeof onSectionChange === 'function') {
            onSectionChange(section);
            return;
        }
        navigate(`/home?tab=orders&orderType=partyHall&partyHallView=${section}`);
    };

    const goToQuoteDetail = (quoteId) => {
        navigate(`/party-hall-quote/${quoteId}`);
    };

    return (
        <>
            <div
                className={`today-order-toolbar${
                    !isOrdersView ? ' today-order-toolbar--quote-actions' : ''
                }`}
            >
                <div className="today-order-toolbar__row">
                    <div className="today-order-toolbar__main">
                        {serviceChannelSelect}
                        <ToolbarRichSelect
                            panelId="party-hall-view-filter"
                            value={isOrdersView ? 'orders' : 'quotes'}
                            onChange={handleSectionChange}
                            options={PARTY_HALL_VIEW_OPTIONS}
                            ariaLabel="Party hall quotes or bookings"
                            panelTitle="Party Hall view"
                        />
                        {!isOrdersView ? (
                            <OrderStatusFilterDropdown
                                panelId="party-hall-quote-status-filter"
                                statusOptions={PARTY_HALL_QUOTE_STATUS_OPTS.filter(
                                    (o) => o.code !== 'CONFIRMED'
                                )}
                                statusSelections={statusSelections}
                                onToggleStatus={toggleStatus}
                                onSetAllStatuses={setAllStatuses}
                            />
                        ) : (
                            <OrderHistoryDateFilters
                                fromDate={fromDate}
                                toDate={toDate}
                                onFromDateChange={setFromDate}
                                onToDateChange={setToDate}
                                ariaLabel="Party hall bookings date range"
                            />
                        )}
                        <button
                            type="button"
                            className="menu-toolbar-btn today-order-toolbar__refresh"
                            onClick={loadQuotes}
                            aria-label="Refresh"
                            title="Refresh"
                            disabled={loading}
                        >
                            <FaSyncAlt aria-hidden="true" />
                        </button>
                    </div>
                    {!isOrdersView ? (
                        <button
                            type="button"
                            className="add-button today-order-toolbar__action"
                            onClick={() => navigate('/party-hall-quote/create')}
                        >
                            {' '}
                            Add quote
                        </button>
                    ) : null}
                </div>
            </div>

            <OrdersTablePanel
                searchValue={search}
                onSearchChange={setSearch}
                resultCount={filteredQuotes.length}
                tableWrapperClassName="today-order-table-container"
            >
                <table className="menu-table today-order-table today-order-table--catering">
                    <thead>
                        <tr>
                            <OrdersSortableTh
                                label={isOrdersView ? 'Booking#' : 'Quote#'}
                                sortKey="orderId"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Customer Name"
                                sortKey="customerName"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Phone"
                                sortKey="customerPhone"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Hall"
                                sortKey="hallName"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Event Date"
                                sortKey="eventDate"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Event Type"
                                sortKey="eventType"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Guests"
                                sortKey="numberOfGuests"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <th>Amenities</th>
                            <OrdersSortableTh
                                label="Estimated ₹"
                                sortKey="totalPrice"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <th>Quoted ₹</th>
                            <OrdersSortableTh
                                label="Status"
                                sortKey="orderStatus"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            <OrdersSortableTh
                                label="Created On"
                                sortKey="createdDate"
                                activeSortKey={sort?.key}
                                sortDirection={sort?.direction}
                                onSortAsc={handleSortAsc}
                                onSortDesc={handleSortDesc}
                            />
                            {!isOrdersView ? <th>Action</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td
                                    colSpan={isOrdersView ? 12 : 13}
                                    className="no-orders-message menu-table-empty-cell"
                                >
                                    Loading party hall {isOrdersView ? 'bookings' : 'quotes'}…
                                </td>
                            </tr>
                        ) : currentQuotes.length > 0 ? (
                            currentQuotes.map((quote) => {
                                const quoteClosed = isTerminalPartyHallQuoteStatus(quote.quoteStatus);
                                return (
                                    <tr key={quote.partyHallQuoteId}>
                                        <td>
                                            <span
                                                className="order-id-link"
                                                onClick={() => goToQuoteDetail(quote.partyHallQuoteId)}
                                            >
                                                {quote.partyHallQuoteId}
                                            </span>
                                        </td>
                                        <td>{quote.customerName || '—'}</td>
                                        <td>
                                            {formatMobileDisplayOrFallback(quote.customerPhone, '—')}
                                        </td>
                                        <td>{quote.hallName || '—'}</td>
                                        <td>{formatDateDisplay(quote.eventDate)}</td>
                                        <td>{quote.eventType || 'N/A'}</td>
                                        <td>{quote.numberOfGuests ?? 'N/A'}</td>
                                        <td>{quote.amenityNotes || '—'}</td>
                                        <td>{formatMoney(quote.estimatedTotal)}</td>
                                        <td>{formatMoney(quote.quotedTotal)}</td>
                                        <td>
                                            <OrderStatusPill
                                                status={quote.quoteStatus ?? 'REQUESTED'}
                                            />
                                        </td>
                                        <td>{formatDateDisplay(quote.createdDate)}</td>
                                        {!isOrdersView ? (
                                            <td className="action-cell menu-table-actions-cell">
                                                {!quoteClosed && (
                                                    <button
                                                        type="button"
                                                        className="action-button"
                                                        title="Edit quote"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openEditQuote(quote);
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                )}
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td
                                    colSpan={isOrdersView ? 12 : 13}
                                    className="no-orders-message menu-table-empty-cell"
                                >
                                    {isOrdersView
                                        ? 'No confirmed party hall bookings yet.'
                                        : 'No party hall quote requests found.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </OrdersTablePanel>

            <OrdersTablePagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={filteredQuotes.length}
                pageSize={ordersPageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={onPageSizeChange}
            />
        </>
    );
};

export default PartyHallPanel;
